import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import { OrganisationGuideline, Guideline } from '../../../../api';

interface OrganisationGuidelinesTableProps {
  guidelines: (OrganisationGuideline & { guideline?: Guideline })[];
  onRemove: (guidelineId: string) => void;
  onRefresh: () => void;
  organisationId: string;
}

const OrganisationGuidelinesTable: React.FC<OrganisationGuidelinesTableProps> = ({
  guidelines,
  onRemove,
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (guidelines.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <DescriptionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No guidelines assigned
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add guidelines to make them available to this organisation
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell><strong>Title</strong></TableCell>
            <TableCell><strong>Version</strong></TableCell>
            <TableCell><strong>Source</strong></TableCell>
            <TableCell><strong>Added</strong></TableCell>
            <TableCell align="right"><strong>Actions</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {guidelines.map((og) => (
            <TableRow
              key={og.organisation_guideline_id}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => window.location.href = `/guidelines/${og.guideline_docs_id}`}
            >
              <TableCell>
                <Typography variant="body2" fontWeight={600}>
                  {og.guideline_title || og.guideline?.title || 'Unknown Guideline'}
                </Typography>
                {og.notes && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {og.notes}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {og.guideline?.version_label || '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {og.guideline?.source_org || '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{formatDate(og.added_at)}</Typography>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Remove from Organisation">
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); onRemove(og.guideline_docs_id); }}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default OrganisationGuidelinesTable;
